import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { ChapterTag, MaskedLine, Reveal } from "@/components/bits";
import Lightbox from "@/components/Lightbox";

const Counter = ({ to, decimals = 0, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, to, {
      duration: 2.2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(v),
    });
    return () => c.stop();
  }, [inView, to]);
  const text = decimals > 0 ? val.toFixed(decimals) : Math.round(val).toLocaleString("en-IN");
  return (
    <span ref={ref} data-testid={`stat-value-${to}`}>
      {text}
      {suffix}
    </span>
  );
};

const FIGURES = [
  { to: 85.5, decimals: 1, suffix: "", unit: "Acres", label: "Total development at Embassy Origins" },
  { to: 6.47, decimals: 2, suffix: "", unit: "Crore Litres", label: "On-site water storage" },
  { to: 4000, decimals: 0, suffix: "+", unit: "Trees", label: "Across 120+ native species" },
];

export default function Destination() {
  const [planOpen, setPlanOpen] = useState(false);
  return (
    <section id="destination" className="py-24 md:py-36" data-testid="destination-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <ChapterTag num="01" label="The Destination" />
        <div className="mt-14 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-5">
            <h2 className="font-display text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              <MaskedLine inView delay={0.05}>A destination shaped by</MaskedLine>
              <MaskedLine inView delay={0.15}>
                <span className="italic text-[#064E3B]">natural intelligence.</span>
              </MaskedLine>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.25} className="space-y-6 text-base leading-relaxed text-[#57534E]">
              <p>
                In a city famed for its innovation, most spaces in Bengaluru lack the most
                vital quality of all — an intelligent, planned, and refined connection to nature.
              </p>
              <p>
                Spread across 85.5 acres near Tarahunise Lake, Embassy Origins is a continuous
                landscape of interconnected water corridors, ecological buffers, and over
                4,000 trees across 120 species.
              </p>
            </Reveal>
          </div>
        </div>
        <Reveal delay={0.2} className="mt-14 md:mt-20">
          <div className="group overflow-hidden rounded-sm" data-testid="destination-visual">
            <img
              src="/images/landscape.jpg"
              alt="Aerial dusk render of South Reserve — nine towers in a flowing arc rising above forest, water and mist"
              data-testid="destination-image"
              className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
            />
          </div>
          <div className="mt-5 flex flex-wrap items-baseline justify-between gap-4">
            <p className="text-xs uppercase tracking-[0.24em] text-[#57534E]">
              Embassy Origins — beside Tarahunise Lake
            </p>
            <button
              onClick={() => setPlanOpen(true)}
              data-testid="explore-masterplan-btn"
              className="group text-[11px] font-semibold uppercase tracking-[0.28em] text-[#064E3B]"
            >
              <span className="border-b border-[#064E3B]/40 pb-1 transition-colors duration-300 group-hover:border-[#A98B5F] group-hover:text-[#A98B5F]">
                Explore the masterplan
              </span>
            </button>
          </div>
        </Reveal>
        <div
          className="mt-16 grid grid-cols-1 border-t border-[#1C1917]/10 md:mt-24 md:grid-cols-3"
          data-testid="stats-section"
        >
          {FIGURES.map((s, i) => (
            <Reveal
              key={s.unit}
              delay={i * 0.12}
              className={`px-2 py-12 md:px-10 md:py-16 ${
                i > 0 ? "border-t border-[#1C1917]/10 md:border-l md:border-t-0" : ""
              }`}
            >
              <div className="font-display text-6xl font-light tracking-tight text-[#1C1917] md:text-7xl">
                <Counter to={s.to} decimals={s.decimals} suffix={s.suffix} />
              </div>
              <div className="mt-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-[#A98B5F]">
                {s.unit}
              </div>
              <p className="mt-4 max-w-[18rem] text-sm leading-relaxed text-[#57534E]">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </div>
      <Lightbox
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        src="/images/masterplan.jpg"
        alt="Master plan of Embassy Origins with the riparian corridor, clubhouses and South Reserve"
        caption="Master plan — Embassy Origins, 85.5 acres"
      />
    </section>
  );
}
